
export default function ArticlePartThree() {
  return (
    <section className="mb-16">
      <div className="prose prose-lg max-w-none">
        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center">
          <i className="ri-shield-check-line text-teal-600 mr-3"></i>
          How to Vet Investors Before Accepting an Offer
        </h2>

        <p className="text-gray-700 leading-relaxed mb-8">
          Not all investors are created equal. While most are legitimate business people looking for fair deals, some may try to take advantage of desperate sellers with lowball offers or questionable tactics. Here's how to protect yourself and ensure you're working with a reputable investor.
        </p>

        <div className="bg-white rounded-xl shadow-md p-8 mb-10 border border-gray-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Essential Vetting Checklist</h3>
          
          <div className="space-y-6">
            <div className="flex items-start">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-search-eye-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">Verify Their Identity and Business</h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Ask for the investor's full legal name, business name, and business license number. Search the Texas Secretary of State website to confirm the business is registered and in good standing. Check if they have a physical office address rather than just a P.O. box.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700"><strong>Red flag:</strong> An investor who refuses to provide business documentation or only communicates through a generic email address.</p>
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-star-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">Check Online Reviews and Reputation</h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Search for the investor or company on Google Reviews, Better Business Bureau, Trustpilot, and Facebook. Look for patterns in reviews—both positive and negative. A few bad reviews aren't necessarily disqualifying, but pay attention to how the company responds to complaints.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700"><strong>What to look for:</strong> Consistent feedback about fair offers, smooth closings, and professional communication. Be wary of companies with no online presence or only brand-new reviews.</p>
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-money-dollar-circle-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">Request Proof of Funds</h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Before accepting an offer, ask the investor to provide proof that they have the cash available to purchase your property. This can be a bank statement, a letter from their lender, or a pre-approval letter if they're using hard money financing.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700"><strong>Why this matters:</strong> You don't want to take your home off the market only to have the deal fall through because the investor couldn't secure funding.</p>
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-user-voice-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">Ask for References</h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  A reputable investor should be able to provide contact information for previous sellers they've worked with. Call these references and ask about their experience, whether the investor honored their offer, and if the closing went smoothly.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700"><strong>Questions to ask references:</strong> Did the investor change the offer at the last minute? Were there any unexpected fees? Would you work with them again?</p>
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-file-text-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">Review the Contract Carefully</h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Never sign a contract without reading it thoroughly or having a real estate attorney review it. Pay special attention to contingencies, assignment clauses, and any fees you're expected to pay. Legitimate investors use standard purchase agreements with clear terms.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700"><strong>Red flag:</strong> Contracts with vague language, excessive contingencies that allow the investor to back out easily, or clauses that give the investor the right to assign the contract to another buyer without your consent.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <img 
          src="https://public.readdy.ai/ai/img_res/5f15f0fb86f9fda98e1bf720c75bf627.jpg"
          alt="Homeowner and investor shaking hands over property agreement"
          className="w-full h-96 object-cover rounded-xl shadow-lg mb-10"
        />

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center">
          <i className="ri-calculator-line text-teal-600 mr-3"></i>
          Understanding Investor Offers: What's Fair?
        </h2>

        <p className="text-gray-700 leading-relaxed mb-8">
          One of the biggest concerns sellers have when working with investors is whether they're getting a fair price. Investor offers are typically lower than retail market value, but that doesn't mean you're being taken advantage of. Here's how to evaluate whether an offer is reasonable.
        </p>

        <div className="bg-teal-50 border-l-4 border-teal-600 p-6 mb-10 rounded-r-lg">
          <h3 className="text-xl font-bold text-gray-900 mb-3">The 70% Rule Explained</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Most fix-and-flip investors use the "70% rule" to determine their maximum offer. This formula is:
          </p>
          <div className="bg-white p-5 rounded-lg mb-4">
            <p className="text-center text-lg font-mono text-gray-900">
              <strong>Offer = (ARV × 0.70) - Repair Costs</strong>
            </p>
            <p className="text-center text-sm text-gray-600 mt-2">
              ARV = After Repair Value (what the home will sell for after renovations)
            </p>
          </div>
          <p className="text-gray-700 leading-relaxed">
            For example, if your home's ARV is $200,000 and it needs $30,000 in repairs, an investor's offer would be around $110,000. This accounts for their renovation costs, holding costs, financing costs, and profit margin.
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-8 mb-10 border border-gray-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Factors That Affect Investor Offers</h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <i className="ri-home-gear-line text-teal-600 mr-2"></i>
                Property Condition
              </h4>
              <p className="text-gray-700 leading-relaxed">
                The more repairs needed, the lower the offer. Major issues like foundation problems, roof damage, or outdated electrical systems significantly impact value. Cosmetic issues like dated kitchens or worn flooring have less impact.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <i className="ri-map-pin-2-line text-teal-600 mr-2"></i>
                Location and Market Conditions
              </h4>
              <p className="text-gray-700 leading-relaxed">
                Properties in desirable El Paso neighborhoods like Westside or East Side command higher offers. Current market conditions, including inventory levels and buyer demand, also play a role in what investors are willing to pay.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <i className="ri-time-line text-teal-600 mr-2"></i>
                Your Timeline
              </h4>
              <p className="text-gray-700 leading-relaxed">
                If you need to close in 7 days versus 30 days, investors may adjust their offer. Faster closings require them to have cash readily available and may limit their ability to secure the best financing terms.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <i className="ri-funds-line text-teal-600 mr-2"></i>
                Investor's Business Model
              </h4>
              <p className="text-gray-700 leading-relaxed">
                Buy-and-hold investors may offer more than fix-and-flip investors because they're focused on long-term rental income rather than quick resale profit. Wholesalers typically offer the least because they need to leave room for their fee and the end buyer's profit.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-8 rounded-xl mb-10">
          <h3 className="text-2xl font-bold mb-4">Should You Accept an Investor Offer?</h3>
          <p className="text-lg leading-relaxed mb-4">
            Compare the investor's offer to what you'd net from a traditional sale after accounting for:
          </p>
          <ul className="space-y-2 mb-4">
            <li className="flex items-start">
              <i className="ri-checkbox-circle-line text-white text-xl mr-2 mt-0.5"></i>
              <span>Real estate agent commission (5-6% of sale price)</span>
            </li>
            <li className="flex items-start">
              <i className="ri-checkbox-circle-line text-white text-xl mr-2 mt-0.5"></i>
              <span>Closing costs (2-3% of sale price)</span>
            </li>
            <li className="flex items-start">
              <i className="ri-checkbox-circle-line text-white text-xl mr-2 mt-0.5"></i>
              <span>Repair costs to make the home market-ready</span>
            </li>
            <li className="flex items-start">
              <i className="ri-checkbox-circle-line text-white text-xl mr-2 mt-0.5"></i>
              <span>Carrying costs (mortgage, utilities, insurance) during the listing period</span>
            </li>
            <li className="flex items-start">
              <i className="ri-checkbox-circle-line text-white text-xl mr-2 mt-0.5"></i>
              <span>Staging and marketing expenses</span>
            </li>
          </ul>
          <p className="text-lg leading-relaxed">
            In many cases, the net proceeds from an investor sale are comparable to or better than a traditional sale, especially when you factor in the time savings and convenience.
          </p>
        </div>

        <img 
          src="https://readdy.ai/api/search-image?query=calculator%20and%20financial%20documents%20on%20desk%20with%20house%20model%20and%20pen%2C%20property%20valuation%20spreadsheet%20visible%2C%20modern%20office%20setting%20with%20natural%20lighting%2C%20professional%20real%20estate%20investment%20analysis%2C%20realistic%20business%20photography%2C%20clean%20organized%20workspace&width=1200&height=600&seq=financial-calculation-006&orientation=landscape"
          alt="Property valuation and financial calculations"
          className="w-full h-96 object-cover rounded-xl shadow-lg mb-10"
        />

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center">
          <i className="ri-question-answer-line text-teal-600 mr-3"></i>
          Common Questions Sellers Ask About Working with Investors
        </h2>

        <div className="space-y-6 mb-10">
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-teal-600">
            <h3 className="text-xl font-bold text-gray-900 mb-3">Do I need to clean or repair anything before selling to an investor?</h3>
            <p className="text-gray-700 leading-relaxed">
              No. One of the main benefits of selling to an investor is that they buy properties in as-is condition. You don't need to clean, make repairs, or even remove personal belongings. The investor will handle all of that after closing. However, removing valuable personal items and doing a basic walkthrough to ensure nothing is left behind is always a good idea.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-teal-600">
            <h3 className="text-xl font-bold text-gray-900 mb-3">How quickly can an investor close on my house?</h3>
            <p className="text-gray-700 leading-relaxed">
              Most cash investors can close in 7 to 14 days, though some can close even faster if needed. The timeline depends on how quickly the title company can complete the title search and prepare closing documents. If you need more time to move out or coordinate logistics, most investors are flexible and can accommodate your preferred closing date.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-teal-600">
            <h3 className="text-xl font-bold text-gray-900 mb-3">Will I have to pay any fees or commissions?</h3>
            <p className="text-gray-700 leading-relaxed">
              Legitimate investors typically cover all closing costs, including title fees, escrow fees, and transfer taxes. You should not have to pay any fees out of pocket. However, you are still responsible for paying off any existing mortgages, liens, or back taxes from the sale proceeds. Always ask for a detailed breakdown of costs before signing a contract.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-teal-600">
            <h3 className="text-xl font-bold text-gray-900 mb-3">Can I get multiple offers from different investors?</h3>
            <p className="text-gray-700 leading-relaxed">
              Absolutely. In fact, we recommend getting at least three offers from different investors to ensure you're getting a competitive price. Each investor has different buying criteria, risk tolerance, and profit margins, so offers can vary significantly. Don't feel pressured to accept the first offer you receive.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-teal-600">
            <h3 className="text-xl font-bold text-gray-900 mb-3">What if I still owe more on my mortgage than the investor's offer?</h3>
            <p className="text-gray-700 leading-relaxed">
              If you're underwater on your mortgage, you may need to pursue a short sale, where the lender agrees to accept less than the full loan balance. Some investors specialize in short sales and can negotiate with your lender on your behalf. Alternatively, you may need to bring cash to closing to cover the difference, though this isn't always feasible for distressed sellers.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
